import Enumerable from "linq";
import { DomainRegistry } from "../../../../base/domain";
import { AuthenticatedService } from "../../../../base/infrastructure";
import { ReferenceFileInformation, IDocumentImageSource, ImageSourceKinds, Document } from "../../../../documaker/domain/document";
import { ISectionBean } from "../../../../documaker/infrastructure/document/ver2/beans";
import {
  IEditableFormatDocumentRepository,
  EditableFormatDocument,
} from "../../../domain/editableFormat";
import { IFormatObjectBean, IEditableFormatDocumentSectionBean } from "../../../infrastructure/editableFormat/ver2/beans";
import { PatientProfileInformation } from "../../../../base/domain/patient";
import { DesignatedDoctorInformation } from "../../../../base/domain/user";
import { HisInformation } from "../../../../base/domain/his";
import { IContentSource } from "../../../../base/domain/drawing";
import { EditableDocumentObjectTranslator } from "../../document/ver2/translators";
import store from "../../../../../../src/store";
import { IConfiguration } from "../../../domain/configuration";
import { EditableFormatTemporaryImageSource, EditableFormatCacheSource } from "../../../domain/editableFormat/sources";

export class EditableFormatDocumentRepository extends AuthenticatedService implements IEditableFormatDocumentRepository {
  constructor(getToken: () => string) {
    super(getToken);
    this.translator = new EditableDocumentObjectTranslator(getToken);
  }
  private translator: EditableDocumentObjectTranslator;

  async getNewDocumentStructure(patientNo: string, formatId: number, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string, archiveId: number): Promise<EditableFormatDocument> {
    const url = "api/format-editor/format/" + formatId + "/document";
    const results = await Promise.all([this.get<any>(
      url,
      {
        params: {
          archiveId: archiveId,
          patientCode: patientNo,
        }
      }
    ),
    this.getPatientProfile(patientNo),
    this.getAllDesignatedDoctors(),
    this.getHisInformation(patientNo, institutionNo, regionalInstitutionNo, hisInfoGetterParameter),
    ]);
    results[0].data.sections = this.applyLayerImageKey(results[0].data);
    const result = this.translator.toDocument(() => this.token, results[0].data);
    result.patientProfile = results[1];
    result.designatedDoctorInformations = results[2];
    result.hisInformation = results[3];
    return this.translator.toEditableFormatDocument(() => this.token, result, null, archiveId);
  }

  applyLayerImageKey(data: any): IEditableFormatDocumentSectionBean[] {
    const formatDocumentSections = data.sections as IEditableFormatDocumentSectionBean[];
    return Enumerable.from(formatDocumentSections)
      .select(s => {
        s.layerImageKey = data.sectionImageKeys[s.formatSectionNumber];
        return s;
      })
      .toArray();
  }

  private async getPatientProfile(patientNo: string): Promise<PatientProfileInformation> {
    return this.fetch(
      DomainRegistry.patient.getPatientProfileRepository(),
      r => r.getPatientProfileInformation(patientNo));
  }

  private async getAllDesignatedDoctors(): Promise<DesignatedDoctorInformation[]> {
    const configuration = store.getters.configuration as IConfiguration;
    if (!configuration.behavior.document.editor.designatedDoctorNumber.isEnable) {
      return [];
    }
    return this.fetch(
      DomainRegistry.user.getDesignatedDoctorRepository(),
      r => r.getAllEntities());
  }

  private async getHisInformation(patientNo: string, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string): Promise<HisInformation> {
    return this.fetch(
      DomainRegistry.his.getHisInformationRepository(),
        r => r.getHisInformation(patientNo, institutionNo, regionalInstitutionNo, hisInfoGetterParameter, null, null));
  }

  private setEditingReferenceFiles(format: IFormatObjectBean): ReferenceFileInformation[] {
    return Enumerable.from(format.referenceFiles)
      .select((r, i) => new ReferenceFileInformation(i, r.fileName))
      .toArray();
  }

  async getNewStoredDocumentStructure(patientNo: string, format: IFormatObjectBean, institutionNo: string, regionalInstitutionNo: string, hisInfoGetterParameter: string): Promise<EditableFormatDocument> {
    const url = "api/format-editor/format/edit-document";
    const results = await Promise.all([this.post(
      url,
      format,
      {
        params: {
          patientCode: patientNo,
        }
      }
    ),
    this.getPatientProfile(patientNo),
    this.getAllDesignatedDoctors(),
    this.getHisInformation(patientNo, institutionNo, regionalInstitutionNo, hisInfoGetterParameter),
    ]);
    results[0].data.sections = this.toIEditableFormatDocumentSectionBean(results[0].data.sections, format);
    const result = this.translator.toDocument(() => this.token, results[0].data);
    result.patientProfile = results[1];
    result.designatedDoctorInformations = results[2];
    result.hisInformation = results[3];
    result.referenceFiles = this.setEditingReferenceFiles(format);
    return this.translator.toEditableFormatDocument(() => this.token, result, format, null);
  }

  toIEditableFormatDocumentSectionBean(documentSections: ISectionBean[], format: IFormatObjectBean): IEditableFormatDocumentSectionBean[] {
    const formatDocumentSections = documentSections as IEditableFormatDocumentSectionBean[];
    return Enumerable.from(formatDocumentSections)
      .select(s => {
        s.layerImageKey = format.sections[s.formatSectionNumber].backgroundImage.imageKey;
        s.layerStoragePlace = format.sections[s.formatSectionNumber].backgroundImage.storagePlace;
        return s;
      })
      .toArray();
  }

  async createImageSource(imageText: string): Promise<IDocumentImageSource> {
    const url = "api/format-editor/temporary/image-save";
    const query = {
      image: imageText,
    };
    const result = await this.post<string>(url, query);
    return new EditableFormatTemporaryImageSource(() => this.token, ImageSourceKinds.Cache, result.data);
  }

  async createNewDocumentPdfSource(document: Document, formatId: number): Promise<IContentSource> {
    const url = "api/format-editor/document/create-pdf-preview-cache/" + formatId;
    const documentBean = this.translator.toSaveDocumentBean(document);
    const result = await this.post(
      url,
      documentBean,
    );
    console.debug(result.data);
    return new EditableFormatCacheSource(() => this.token, result.data);
  }

  async createNewStoredDocumentPdfSource(document: Document, format: IFormatObjectBean): Promise<IContentSource> {
    const url = "api/format-editor/create-pdf-preview-cache";
    const documentBean = this.translator.toSaveDocumentBean(document);
    const sendData = {
      token: this.token,
      document: documentBean,
      format: format,
    };
    const result = await this.post(
      url,
      sendData,
    );
    console.debug(result.data);
    return new EditableFormatCacheSource(() => this.token, result.data);
  }

  async createNewDocumentPrintPdfSource(document: Document, formatId: number): Promise<IContentSource> {
    const url = "api/format-editor/document/create-pdf-print-cache/" + formatId;
    const documentBean = this.translator.toSaveDocumentBean(document);
    const result = await this.post(
      url,
      documentBean,
    );
    console.debug(result.data);
    return new EditableFormatCacheSource(() => this.token, result.data);
  }

  async createNewStoredDocumentPrintPdfSource(document: Document, format: IFormatObjectBean): Promise<IContentSource> {
    const url = "api/format-editor/create-pdf-print-cache";
    const documentBean = this.translator.toSaveDocumentBean(document);
    const sendData = {
      token: this.token,
      document: documentBean,
      format: format,
    };
    const result = await this.post(
      url,
      sendData,
    );
    console.debug(result.data);
    return new EditableFormatCacheSource(() => this.token, result.data);
  }
}
